import React, { useState, useEffect } from 'react';
import player1Image from '../../assets/images/player_1.png';
import player2Image from '../../assets/images/player_2.png';
import { BOARD_SIZE } from '../../constants';

// Predefined stone positions for the animation
const stonePositions = [
  { row: 0, col: 0, color: 'black' },
  { row: BOARD_SIZE - 1, col: BOARD_SIZE - 1, color: 'white' },
  { row: 0, col: BOARD_SIZE - 1, color: 'black' },
  { row: BOARD_SIZE - 1, col: 0, color: 'white' },
  { row: Math.floor(BOARD_SIZE / 2) - 1, col: Math.floor(BOARD_SIZE / 2) - 1, color: 'black' },
  { row: Math.floor(BOARD_SIZE / 2), col: Math.floor(BOARD_SIZE / 2), color: 'white' },
  { row: Math.floor(BOARD_SIZE / 2) - 1, col: Math.floor(BOARD_SIZE / 2), color: 'black' },
  { row: Math.floor(BOARD_SIZE / 2), col: Math.floor(BOARD_SIZE / 2) - 1, color: 'white' },
];

const CELL_SIZE = 32;

function PlacingStones() {
  const [currentStep, setCurrentStep] = useState(0);
  const [board, setBoard] = useState(Array(BOARD_SIZE).fill(null).map(() => Array(BOARD_SIZE).fill(null)));

  useEffect(() => {
    let interval;
    let timeout;

    const startAnimation = () => {
      // Clear any existing timeouts/intervals
      if (interval) clearInterval(interval);
      if (timeout) clearTimeout(timeout);

      // Reset state
      setBoard(Array(BOARD_SIZE).fill(null).map(() => Array(BOARD_SIZE).fill(null)));
      setCurrentStep(0);

      // Place stones one by one
      interval = setInterval(() => {
        setCurrentStep(prev => {
          if (prev >= stonePositions.length) {
            clearInterval(interval);
            // Wait 2 seconds before restarting
            timeout = setTimeout(startAnimation, 2000);
            return prev;
          }

          const { row, col, color } = stonePositions[prev];
          setBoard(prevBoard => {
            const newBoard = prevBoard.map(row => [...row]);
            newBoard[row][col] = color;
            return newBoard;
          });

          return prev + 1;
        });
      }, 1000); // Place a stone every second
    };

    startAnimation();

    // Cleanup function
    return () => {
      if (interval) clearInterval(interval);
      if (timeout) clearTimeout(timeout);
    };
  }, []); // Empty dependency array since stonePositions is constant

  return (
    <div className="flex justify-center w-full">
      <div className="flex flex-col sm:flex-row items-center justify-between w-full max-w-2xl gap-4 sm:gap-0">
        {/* Mobile Players (above board) */}
        <div className="flex sm:hidden items-center justify-center gap-8 w-full">
          {/* Player 1 */}
          <div className="flex flex-col items-center">
            <img src={player1Image} alt="Player 1" className="w-16 h-16 object-contain" />
            <div className="flex gap-1.5 mt-2">
              {Array(4).fill(null).map((_, i) => (
                <div
                  key={`mobile-1-${i}`}
                  className={`shadow-sm shadow-white w-4 h-4 rounded-full bg-black ${currentStep > i * 2 ? 'opacity-20' : ''}`}
                />
              ))}
            </div>
          </div>

          {/* Player 2 */}
          <div className="flex flex-col items-center">
            <img src={player2Image} alt="Player 2" className="w-16 h-16 object-contain" />
            <div className="flex gap-1.5 mt-2">
              {Array(4).fill(null).map((_, i) => (
                <div
                  key={`mobile-2-${i}`}
                  className={`shadow-sm shadow-black w-4 h-4 rounded-full bg-white ${currentStep > i * 2 + 1 ? 'opacity-20' : ''}`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Desktop Players (beside board) */}
        {/* Player 1 */}
        <div className="hidden sm:flex flex-col items-center">
          <img src={player1Image} alt="Player 1" className="w-24 h-24 object-contain" />
          <div className="flex gap-1.5 mt-2">
            {Array(4).fill(null).map((_, i) => (
              <div
                key={`desktop-1-${i}`}
                className={`shadow-sm shadow-white w-5 h-5 rounded-full bg-black ${currentStep > i * 2 ? 'opacity-20' : ''}`}
              />
            ))}
          </div>
        </div>

        {/* Board */}
        <div className="relative rounded-lg p-4" style={{ backgroundColor: '#e1c083' }}>
          <div className="grid gap-0" style={{
            gridTemplateColumns: `repeat(${BOARD_SIZE}, minmax(0, 1fr))`,
            width: `${BOARD_SIZE * CELL_SIZE}px`,
            height: `${BOARD_SIZE * CELL_SIZE}px`
          }}>
            {board.map((row, rowIndex) =>
              row.map((cell, colIndex) => (
                <div
                  key={`${rowIndex}-${colIndex}`}
                  className="relative w-8 h-8"
                >
                  {/* Grid lines */}
                  <div className="absolute inset-0 pointer-events-none">
                    {rowIndex < BOARD_SIZE - 1 && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#99804a]" />
                    )}
                    {colIndex < BOARD_SIZE - 1 && (
                      <div className="absolute right-0 top-0 bottom-0 w-0.5 bg-[#99804a]" />
                    )}
                  </div>
                  {/* Stone */}
                  {cell && (
                    <div className={`
                      absolute inset-2 rounded-full
                      ${cell === 'black'
                        ? 'bg-black shadow-sm shadow-white'
                        : 'bg-white shadow-sm shadow-black'
                      }
                      animate-[popIn_0.3s_ease-out]
                    `} />
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Player 2 (Desktop) */}
        <div className="hidden sm:flex flex-col items-center">
          <img src={player2Image} alt="Player 2" className="w-24 h-24 object-contain" />
          <div className="flex gap-1.5 mt-2">
            {Array(4).fill(null).map((_, i) => (
              <div
                key={`desktop-2-${i}`}
                className={`shadow-sm shadow-black w-5 h-5 rounded-full bg-white ${currentStep > i * 2 + 1 ? 'opacity-20' : ''}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PlacingStones; 
